import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import type { SensorAlertBundle } from '@mark43/cad-api';
import {
    Box,
    Button,
    ButtonGroup,
    Divider,
    Flex,
    IconButton,
    Tag,
    Text,
    Tooltip,
    VStack,
} from '@cad-arc';
import {
    formatSensorAlertClipTime,
    getSensorAlertObjectType,
    getSensorAlertTags,
    NearbySensor,
} from './helpers';
import { SensorAlertThumbnail } from './sensor-alert-thumbnail';
import { SENSOR_GRID_RAIL_MIN_WIDTH, SENSOR_GRID_RAIL_WIDTH } from './sensor-grid-layout';
import { useArchiveSensorAlert, useArchiveSensorAlertsWithUndo } from './use-archive-sensor-alert';
import { useOpenSensorEventPanel } from './use-open-sensor-event-panel';
import { useSensorsStore } from './use-sensors-store';

const THUMBNAIL_SIZE = 44;

function SensorGridEventRow({
    alert,
    rankBySensorId,
    isArchivedView,
}: {
    alert: SensorAlertBundle;
    rankBySensorId: Map<string, NearbySensor>;
    isArchivedView: boolean;
}): React.ReactElement {
    const openSensorEventPanel = useOpenSensorEventPanel();
    const archiveSensorAlert = useArchiveSensorAlert();

    const sensor = rankBySensorId.get(alert.sensorExternalId);
    const tags = getSensorAlertTags(alert);
    const title = alert.alertName ?? getSensorAlertObjectType(alert) ?? 'Sensor event';

    return (
        <Flex gap={2} padding={2} alignItems="flex-start">
            {/* A single decoded frame, not the gif — nine animating frames per row at 10.5 MB
                resident each is not worth it at 44px. See `SensorAlertThumbnail`. */}
            <SensorAlertThumbnail
                imageUrl={alert.imageUrl}
                width={THUMBNAIL_SIZE}
                height={THUMBNAIL_SIZE}
            />
            <VStack align="stretch" spacing={0} minWidth={0} flexGrow={1}>
                <Flex alignItems="baseline" gap={2}>
                    <Text variant="bodyMd" fontWeight="bold" noOfLines={1}>
                        {title}
                    </Text>
                    <Box marginLeft="auto" flexShrink={0}>
                        <Text variant="caption" fontFamily="mono" color="secondary">
                            {formatSensorAlertClipTime(alert.createdDateUtc) ?? '--:--'}
                        </Text>
                    </Box>
                </Flex>
                {/* Stamped with the tile's rank and distance so a row can be traced back to it. */}
                <Text variant="caption" color="secondary" noOfLines={1}>
                    {sensor
                        ? `${sensor.rank} · ${sensor.name ?? sensor.externalId}`
                        : 'Unknown sensor'}
                </Text>
                <Flex gap={1} flexWrap="wrap" paddingTop={1}>
                    {tags.map((tag) => (
                        <Tag key={tag} size="sm">
                            {tag}
                        </Tag>
                    ))}
                </Flex>
                <Flex gap={2} alignItems="center" paddingTop={1}>
                    {/* Same treatment as every other Open sensor event in the feature. */}
                    <Button
                        size="sm"
                        trailingVisual="OpenNewWindow"
                        onClick={() => openSensorEventPanel({ alertKey: alert.alertKey })}
                    >
                        Open sensor event
                    </Button>
                    <Box marginLeft="auto">
                        {/* Icon only — a word here competed with the action beside it. The
                            tooltip carries the label a bare icon cannot. */}
                        <Tooltip content={isArchivedView ? 'Restore' : 'Archive'}>
                            <IconButton
                                size="sm"
                                variant="ghost"
                                icon="Archive"
                                aria-label={isArchivedView ? 'Restore' : 'Archive'}
                                onClick={() => archiveSensorAlert(alert, !isArchivedView)}
                            />
                        </Tooltip>
                    </Box>
                </Flex>
            </VStack>
        </Flex>
    );
}

/**
 * Sensor events merged across every camera in the grid, newest first.
 *
 * Keyed off a `Set` of sensor ids rather than the tile list, because the grid repeats cameras to
 * fill nine tiles and a repeated camera must not contribute its events twice.
 */
export const SensorGridEventsRail = observer(function SensorGridEventsRail({
    sensors,
}: {
    sensors: NearbySensor[];
}): React.ReactElement {
    const sensorsStore = useSensorsStore();
    const archiveWithUndo = useArchiveSensorAlertsWithUndo();
    const [isArchivedView, setIsArchivedView] = useState(false);

    const rankBySensorId = new Map(sensors.map((sensor) => [sensor.externalId, sensor]));
    const sensorIds = new Set(rankBySensorId.keys());
    const alerts = sensorsStore.getAlertsForSensors(sensorIds, { archived: isArchivedView });
    const activeCount = sensorsStore.getAlertsForSensors(sensorIds, { archived: false }).length;

    return (
        /*
         * Takes the grid's height rather than adding its own: the rail fills a fixed-width box
         * from inside it, so a long list scrolls instead of growing a panel that is as tall as its
         * content.
         */
        <Box
            position="relative"
            flexShrink={0}
            width={`${SENSOR_GRID_RAIL_WIDTH}px`}
            minWidth={`${SENSOR_GRID_RAIL_MIN_WIDTH}px`}
            borderLeft="sm"
            borderColor="border.default"
        >
            <VStack position="absolute" inset={0} align="stretch" spacing={0}>
                <VStack align="stretch" spacing={2} padding={2}>
                    <Flex alignItems="baseline" gap={2}>
                        <Text variant="headingXs">Sensor events</Text>
                        <Box marginLeft="auto">
                            <Text variant="caption" fontFamily="mono" color="secondary">
                                {activeCount}
                            </Text>
                        </Box>
                    </Flex>
                    {/* A segmented control rather than Arc `Tabs`: this is a filter over one list,
                    and `Tabs` would put a second tab strip inside a panel that has a header
                    already. `isAttached` is what makes the two read as one control. */}
                    <ButtonGroup isAttached size="sm">
                        <Button
                            variant={isArchivedView ? 'outline' : 'solid'}
                            onClick={() => setIsArchivedView(false)}
                        >
                            Active
                        </Button>
                        <Button
                            variant={isArchivedView ? 'solid' : 'outline'}
                            onClick={() => setIsArchivedView(true)}
                        >
                            Archived
                        </Button>
                    </ButtonGroup>
                </VStack>
                <Divider />

                <Box flexGrow={1} minHeight={0} overflowY="auto">
                    {alerts.length === 0 ? (
                        <Box padding={2}>
                            <Text variant="caption" color="secondary">
                                {isArchivedView
                                    ? 'Nothing archived yet.'
                                    : 'No sensor events from these sensors yet.'}
                            </Text>
                        </Box>
                    ) : (
                        <VStack align="stretch" spacing={0} divider={<Divider />}>
                            {alerts.map((alert) => (
                                <SensorGridEventRow
                                    key={alert.alertKey}
                                    alert={alert}
                                    rankBySensorId={rankBySensorId}
                                    isArchivedView={isArchivedView}
                                />
                            ))}
                        </VStack>
                    )}
                </Box>

                <Divider />
                <Flex padding={2} justifyContent="flex-end">
                    {/* Disabled rather than hidden when the list is empty, so the footer does not
                    change height as events arrive and clear. */}
                    <Button
                        size="sm"
                        variant="ghost"
                        leadingVisual="Archive"
                        isDisabled={alerts.length === 0}
                        onClick={() => {
                            /*
                             * One request for the lot. This used to send one per alert, and since each
                             * answer is the whole archived set applied wholesale, whichever landed last
                             * won — rows archived on the click reappeared a moment later.
                             */
                            archiveWithUndo(alerts, !isArchivedView);
                        }}
                    >
                        {isArchivedView ? 'Restore all' : 'Archive all'}
                    </Button>
                </Flex>
            </VStack>
        </Box>
    );
});
